// Copyright 2026 FlairCV Inc.
// SPDX-License-Identifier: LicenseRef-FlairCV-Enterprise

/**
 * Enterprise app layout — identical to the OSS layout but adds:
 *   - HydrationOverlay (restoring data from cloud after purge)
 *   - VaultPassphraseDialog (OIDC users without a passphrase)
 *
 * These are rendered as portals/overlays that sit on top of the layout.
 */

import { Menu } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from '@tanstack/react-router';
import { motion, AnimatePresence } from 'motion/react';
import { AppSidebar } from './app-sidebar';
import { HydrationOverlay } from './hydration-overlay';
import { VaultRelockBanner } from './vault-relock-banner';
import { ImpersonationBanner } from './impersonation-banner_enterprise';

interface AppLayoutProps {
  children: React.ReactNode;
}

const SIDEBAR_MINIMISED_KEY = 'flaircv-sidebar-minimised';

export function AppLayout({ children }: AppLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(() =>
    typeof window !== 'undefined'
      ? window.matchMedia('(min-width: 1024px)').matches
      : false,
  );

  // Desktop minimised state — persisted to localStorage.
  const [minimised, setMinimised] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(SIDEBAR_MINIMISED_KEY) === 'true';
  });

  function toggleMinimised() {
    setMinimised((prev) => {
      const next = !prev;
      localStorage.setItem(SIDEBAR_MINIMISED_KEY, String(next));
      return next;
    });
  }

  // Close sidebar on route change — only on mobile (< lg breakpoint).
  const { pathname } = useLocation();
  const prevPathRef = useRef(pathname);

  useEffect(() => {
    // Skip the initial render — only react to actual route changes.
    if (prevPathRef.current === pathname) return;
    prevPathRef.current = pathname;

    if (typeof window !== 'undefined' && !window.matchMedia('(min-width: 1024px)').matches) {
      setSidebarOpen(false);
    }
  }, [pathname]);

  return (
    <div className="flex h-screen overflow-hidden bg-background lg:p-2">
      {/* Skip-to-content link (WCAG 2.1 AA) */}
      <a href="#main-content" className="skip-to-content">
        Skip to content
      </a>

      {/* Mobile overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm lg:hidden"
            onClick={() => setSidebarOpen(false)}
            aria-hidden="true"
          />
        )}
      </AnimatePresence>

      {/* Floating sidebar — glassmorphism surface, separated from viewport edges */}
      <div className="z-50 lg:relative lg:z-10 lg:h-full">
        <div className="fixed inset-y-0 left-0 p-2 lg:static lg:h-full lg:p-0">
          <AppSidebar
            collapsed={!sidebarOpen}
            minimised={minimised}
            onToggle={() => setSidebarOpen(!sidebarOpen)}
            onToggleMinimise={toggleMinimised}
          />
        </div>
      </div>

      {/* Main content area */}
      <div className="flex flex-1 flex-col overflow-hidden bg-background lg:ml-2 lg:rounded-lg lg:border lg:border-border lg:bg-card/50 lg:backdrop-blur-sm">
        {/* Mobile-only top bar — burger menu toggle */}
        <header className="flex h-12 items-center px-4 border-b border-border/50 lg:hidden">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            aria-label="Toggle sidebar"
          >
            <Menu className="h-5 w-5" />
          </button>
        </header>

        {/* Scrollable content */}
        <main
          id="main-content"
          className="flex-1 overflow-y-auto"
          role="main"
        >
          <div className="mx-auto max-w-6xl px-3 py-4 sm:px-4 sm:py-6 lg:px-8">
            <ImpersonationBanner />
            <VaultRelockBanner />
            {children}
          </div>
        </main>
      </div>

      {/* Enterprise overlays */}
      <HydrationOverlay />
    </div>
  );
}
